import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import {
  INTAKE_STEP_IDS,
  INTAKE_STEP_LABELS,
  type IntakeStepId,
} from '../../../lib/intake-draft.js';
import type { IntakeShellRenderProps } from './IntakeShell.js';

/**
 * wd-2.3.11 — Step 10 "Review & submit" for the prospect-facing intake
 * portal.
 *
 * Fields mirror `intakeStep10Schema` in `packages/config/src/intake-steps.ts`
 * — `reviewedAllSteps` + `moneyBackGuaranteeAcknowledged` + `tosAcknowledged`
 * + `privacyAcknowledged` as four literal-true gates, `ownerSignature`
 * (printedName + email + auto-stamped timestamp), and `renderedKitHash`
 * computed client-side as SHA-256 of the serialized payload for the
 * TOCTOU check the design doc §7.10 requires. The server re-computes its
 * own hash at submit time — a mismatch signals a stale client view.
 *
 * Soft-blocker acknowledgements default to `[]`. Actual soft-blocker
 * detection is deferred to a follow-up ticket (lands with per-step
 * completeness scoring). Until then the schema accepts the empty array.
 *
 * The five summary cards (steps 1, 2, 5, 7, 8) cover the fields the
 * reviewer needs to see at a glance. Each card exposes an "Edit" button
 * that routes back to the owning step via the shell's `onGoToStep`
 * render prop. The remaining steps (3, 4, 6, 9) are collapsed into a
 * shared "Other sections" card that still surfaces the Edit deep-link
 * without a detailed summary — keeps the review page short on mobile.
 *
 * `ipAddress` is never set client-side — the server populates it at
 * `POST /step` time per design §7.10.
 */

type OwnerSignatureDraft = {
  printedName: string;
  email: string;
  timestamp: string;
};

type Step10Draft = {
  reviewedAllSteps: boolean;
  moneyBackGuaranteeAcknowledged: boolean;
  tosAcknowledged: boolean;
  privacyAcknowledged: boolean;
  renderedKitHash: string;
  ownerSignature: OwnerSignatureDraft;
};

const EMPTY_SIGNATURE: OwnerSignatureDraft = {
  printedName: '',
  email: '',
  timestamp: '',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringField(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readSignature(value: unknown): OwnerSignatureDraft {
  if (!isRecord(value)) return { ...EMPTY_SIGNATURE };
  return {
    printedName: stringField(value.printedName),
    email: stringField(value.email),
    timestamp: stringField(value.timestamp),
  };
}

function readStep10(draft: Record<string, unknown>): Step10Draft {
  return {
    reviewedAllSteps: draft.reviewedAllSteps === true,
    moneyBackGuaranteeAcknowledged:
      draft.moneyBackGuaranteeAcknowledged === true,
    tosAcknowledged: draft.tosAcknowledged === true,
    privacyAcknowledged: draft.privacyAcknowledged === true,
    renderedKitHash: stringField(draft.renderedKitHash),
    ownerSignature: readSignature(draft.ownerSignature),
  };
}

function groupIssuesByPath(
  issues: ReadonlyArray<{ path: string; message: string }>,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const issue of issues) {
    const key = issue.path === '' ? '_root' : issue.path;
    const list = out[key] ?? [];
    list.push(issue.message);
    out[key] = list;
  }
  return out;
}

export function validateSignatureEmail(
  raw: string,
): { ok: true } | { ok: false; reason: 'empty' | 'malformed' } {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: false, reason: 'empty' };
  if (!EMAIL_REGEX.test(trimmed)) return { ok: false, reason: 'malformed' };
  return { ok: true };
}

export function describeSignatureEmailError(
  reason: 'empty' | 'malformed',
): string {
  switch (reason) {
    case 'empty':
      return 'Enter your email so we can match it to your invite.';
    case 'malformed':
      return 'That doesn’t look like a valid email.';
  }
}

/** Canonicalize a payload before hashing so whitespace / key order doesn't
 *  flap the hash between keystrokes. Keys sorted recursively. */
export function canonicalizePayload(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((v) => canonicalizePayload(v));
  }
  if (isRecord(value)) {
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(value).sort()) {
      sorted[key] = canonicalizePayload(value[key]);
    }
    return sorted;
  }
  return value;
}

function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) {
    out += b.toString(16).padStart(2, '0');
  }
  return out;
}

/** Browser / Node 19+ SHA-256 via `crypto.subtle`. Returns 64-char hex. */
export async function computeRenderedKitHash(
  payloadWithoutStep10: Record<string, unknown>,
): Promise<string> {
  const canonical = canonicalizePayload(payloadWithoutStep10);
  const json = JSON.stringify(canonical);
  const bytes = new TextEncoder().encode(json);
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    // Extremely defensive — the admin-app bundle targets browsers that
    // support SubtleCrypto since ~2020. Return a deterministic
    // placeholder so the Zod length check still fires (64 chars).
    return '0'.repeat(64);
  }
  const digest = await subtle.digest('SHA-256', bytes);
  return bytesToHex(new Uint8Array(digest));
}

function formatCurrency(cents: unknown): string {
  if (typeof cents !== 'number') return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function isStepComplete(
  payload: Record<string, unknown>,
  stepId: IntakeStepId,
): boolean {
  const key = `step${stepId}` as const;
  const entry = payload[key];
  return isRecord(entry) && Object.keys(entry).length > 0;
}

export function Step10Review({
  stepDraft,
  onChange,
  intake,
  issues,
  onGoToStep,
}: IntakeShellRenderProps): JSX.Element {
  const value = useMemo(() => readStep10(stepDraft), [stepDraft]);
  const errorsByPath = useMemo(() => groupIssuesByPath(issues), [issues]);

  // Snapshot of the full payload (steps 1-9) used for review + hash.
  const priorPayload = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const id of INTAKE_STEP_IDS) {
      if (id === '10') continue;
      const key = `step${id}` as const;
      if (intake.payload[key] !== undefined) {
        out[key] = intake.payload[key];
      }
    }
    return out;
  }, [intake.payload]);

  const priorPayloadSerialized = useMemo(
    () => JSON.stringify(canonicalizePayload(priorPayload)),
    [priorPayload],
  );

  // Recompute the kit hash whenever the upstream payload changes.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const hash = await computeRenderedKitHash(priorPayload);
      if (cancelled) return;
      if (hash !== value.renderedKitHash) {
        onChange({ renderedKitHash: hash });
      }
    })();
    return () => {
      cancelled = true;
    };
    // `priorPayloadSerialized` is the stable dependency; `value.renderedKitHash`
    // is deliberately excluded since we only want to react to upstream edits,
    // not our own write-back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorPayloadSerialized]);

  // Auto-stamp the signature timestamp once printed name + email are valid
  // — but only if the timestamp isn't already set. A later edit-then-fix
  // doesn't advance the stamp.
  useEffect(() => {
    if (value.ownerSignature.timestamp !== '') return;
    const nameOk = value.ownerSignature.printedName.trim().length >= 2;
    const emailOk = validateSignatureEmail(value.ownerSignature.email).ok;
    if (!nameOk || !emailOk) return;
    onChange({
      ownerSignature: {
        ...value.ownerSignature,
        timestamp: new Date().toISOString(),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.ownerSignature.printedName, value.ownerSignature.email]);

  const onCheckbox
    = (field: keyof Pick<Step10Draft,
        | 'reviewedAllSteps'
        | 'moneyBackGuaranteeAcknowledged'
        | 'tosAcknowledged'
        | 'privacyAcknowledged'
      >) =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      // The schema's literal(true) only accepts true. Emit true when
      // checked and undefined (remove) when unchecked so the payload
      // stays schema-clean.
      if (e.target.checked) {
        onChange({ [field]: true });
      } else {
        onChange({ [field]: undefined });
      }
    };

  const onSignatureField
    = (field: keyof OwnerSignatureDraft) =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      onChange({
        ownerSignature: { ...value.ownerSignature, [field]: e.target.value },
      });
    };

  const inlineError = (path: string): string | null => {
    const list = errorsByPath[path] ?? [];
    return list.length > 0 ? list.join(' · ') : null;
  };

  const printedNameError = inlineError('ownerSignature.printedName');
  const emailServerError = inlineError('ownerSignature.email');
  const hashServerError = inlineError('renderedKitHash');
  const reviewedAllError = inlineError('reviewedAllSteps');
  const mbgAckError = inlineError('moneyBackGuaranteeAcknowledged');
  const tosAckError = inlineError('tosAcknowledged');
  const privacyAckError = inlineError('privacyAcknowledged');
  const sigRootError = inlineError('ownerSignature');

  const emailCheck = validateSignatureEmail(value.ownerSignature.email);
  const clientEmailError
    = value.ownerSignature.email !== '' && !emailCheck.ok
      ? describeSignatureEmailError(emailCheck.reason)
      : null;

  const step1 = isRecord(intake.payload.step1) ? intake.payload.step1 : {};
  const step2 = isRecord(intake.payload.step2) ? intake.payload.step2 : {};
  const step5 = isRecord(intake.payload.step5) ? intake.payload.step5 : {};
  const step7 = isRecord(intake.payload.step7) ? intake.payload.step7 : {};
  const step8 = isRecord(intake.payload.step8) ? intake.payload.step8 : {};

  const services = Array.isArray(step7.services) ? step7.services : [];
  const staff = Array.isArray(step5.staff) ? step5.staff : [];
  const modules = Array.isArray(step8.modules) ? step8.modules : [];

  return (
    <form
      className="ferro-form ferro-intake__form"
      aria-label="Step 10 — Review and submit"
      noValidate
      onSubmit={(e) => e.preventDefault()}
    >
      <p className="ferro-muted">
        One last look. Confirm the details below, sign, and submit for
        provisioning. Our team reviews every intake within one business day.
      </p>

      <section
        className="ferro-intake__reviewCard"
        aria-labelledby="intake-step10-card-business"
        data-testid="intake-step10-card-business"
      >
        <header className="ferro-intake__reviewCardHeader">
          <h2
            id="intake-step10-card-business"
            className="ferro-intake__reviewCardTitle"
          >
            Business basics
          </h2>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => onGoToStep('1')}
            data-testid="intake-step10-edit-1"
          >
            Edit
          </button>
        </header>
        <dl className="ferro-intake__reviewList">
          <div>
            <dt>Legal name</dt>
            <dd data-testid="intake-step10-summary-legalName">
              {stringField(step1.legalName) || '—'}
            </dd>
          </div>
          <div>
            <dt>Display name</dt>
            <dd data-testid="intake-step10-summary-displayName">
              {stringField(step1.displayName) || '—'}
            </dd>
          </div>
          <div>
            <dt>Subdomain slug</dt>
            <dd data-testid="intake-step10-summary-slug">
              {stringField(step1.slug) || '—'}
            </dd>
          </div>
          <div>
            <dt>Timezone</dt>
            <dd data-testid="intake-step10-summary-timezone">
              {stringField(step1.timezone) || '—'}
            </dd>
          </div>
          <div>
            <dt>Entity type</dt>
            <dd data-testid="intake-step10-summary-entityType">
              {stringField(step1.entityType) || '—'}
            </dd>
          </div>
        </dl>
      </section>

      <section
        className="ferro-intake__reviewCard"
        aria-labelledby="intake-step10-card-brand"
        data-testid="intake-step10-card-brand"
      >
        <header className="ferro-intake__reviewCardHeader">
          <h2
            id="intake-step10-card-brand"
            className="ferro-intake__reviewCardTitle"
          >
            Brand kit
          </h2>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => onGoToStep('2')}
            data-testid="intake-step10-edit-2"
          >
            Edit
          </button>
        </header>
        <dl className="ferro-intake__reviewList">
          <div>
            <dt>Primary color</dt>
            <dd data-testid="intake-step10-summary-primaryColor">
              {isRecord(step2.colors)
                ? stringField(step2.colors.primary) || '—'
                : '—'}
            </dd>
          </div>
          <div>
            <dt>Company name</dt>
            <dd data-testid="intake-step10-summary-companyName">
              {isRecord(step2.copy)
                ? stringField(step2.copy.companyName) || '—'
                : '—'}
            </dd>
          </div>
          <div>
            <dt>Support email</dt>
            <dd data-testid="intake-step10-summary-supportEmail">
              {isRecord(step2.copy)
                ? stringField(step2.copy.supportEmail) || '—'
                : '—'}
            </dd>
          </div>
        </dl>
      </section>

      <section
        className="ferro-intake__reviewCard"
        aria-labelledby="intake-step10-card-staff"
        data-testid="intake-step10-card-staff"
      >
        <header className="ferro-intake__reviewCardHeader">
          <h2
            id="intake-step10-card-staff"
            className="ferro-intake__reviewCardTitle"
          >
            Staff roster
          </h2>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => onGoToStep('5')}
            data-testid="intake-step10-edit-5"
          >
            Edit
          </button>
        </header>
        <p
          className="ferro-muted"
          data-testid="intake-step10-summary-staffCount"
        >
          {staff.length === 1 ? '1 staff member' : `${staff.length} staff members`}
        </p>
      </section>

      <section
        className="ferro-intake__reviewCard"
        aria-labelledby="intake-step10-card-catalog"
        data-testid="intake-step10-card-catalog"
      >
        <header className="ferro-intake__reviewCardHeader">
          <h2
            id="intake-step10-card-catalog"
            className="ferro-intake__reviewCardTitle"
          >
            Service catalog
          </h2>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => onGoToStep('7')}
            data-testid="intake-step10-edit-7"
          >
            Edit
          </button>
        </header>
        {services.length === 0 ? (
          <p
            className="ferro-muted"
            data-testid="intake-step10-summary-servicesEmpty"
          >
            No services configured yet.
          </p>
        ) : (
          <ul
            className="ferro-intake__reviewSubList"
            data-testid="intake-step10-summary-services"
          >
            {services.slice(0, 6).map((svc, idx) => {
              if (!isRecord(svc)) return null;
              return (
                <li
                  key={`${stringField(svc.slug)}-${idx}`}
                  data-testid={`intake-step10-summary-service-${idx}`}
                >
                  <strong>{stringField(svc.displayName) || stringField(svc.slug)}</strong>
                  <span className="ferro-muted">
                    {' '}
                    {formatCurrency(svc.priceCents)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section
        className="ferro-intake__reviewCard"
        aria-labelledby="intake-step10-card-modules"
        data-testid="intake-step10-card-modules"
      >
        <header className="ferro-intake__reviewCardHeader">
          <h2
            id="intake-step10-card-modules"
            className="ferro-intake__reviewCardTitle"
          >
            Active modules
          </h2>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => onGoToStep('8')}
            data-testid="intake-step10-edit-8"
          >
            Edit
          </button>
        </header>
        <p
          className="ferro-muted"
          data-testid="intake-step10-summary-modules"
        >
          {modules.length === 0
            ? 'No modules selected yet.'
            : modules
                .filter((m): m is string => typeof m === 'string')
                .join(', ')}
        </p>
      </section>

      <section
        className="ferro-intake__reviewCard"
        aria-labelledby="intake-step10-card-other"
        data-testid="intake-step10-card-other"
      >
        <header className="ferro-intake__reviewCardHeader">
          <h2
            id="intake-step10-card-other"
            className="ferro-intake__reviewCardTitle"
          >
            Other sections
          </h2>
        </header>
        <ul className="ferro-intake__reviewSubList">
          {(['3', '4', '6', '9'] as const).map((id) => (
            <li
              key={id}
              data-testid={`intake-step10-other-${id}`}
            >
              <span>{INTAKE_STEP_LABELS[id]}</span>
              <span className="ferro-muted">
                {' — '}
                {isStepComplete(intake.payload, id) ? 'complete' : 'incomplete'}
              </span>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => onGoToStep(id)}
                data-testid={`intake-step10-edit-${id}`}
              >
                Edit
              </button>
            </li>
          ))}
        </ul>
      </section>

      <fieldset
        className="ferro-field ferro-intake__signature"
        data-testid="intake-step10-signature"
      >
        <legend className="ferro-field__label">Owner signature</legend>
        <p className="ferro-muted">
          Only the tenant owner can submit. We match this email to the invite
          recipient at submit time.
        </p>

        <label className="ferro-field">
          <span className="ferro-field__label">Printed name</span>
          <input
            type="text"
            name="ownerSignature.printedName"
            required
            autoComplete="name"
            value={value.ownerSignature.printedName}
            onChange={onSignatureField('printedName')}
            className="ferro-field__input"
            placeholder="Jane Owner"
            aria-invalid={printedNameError ? true : undefined}
            data-testid="intake-step10-printed-name"
          />
          {printedNameError ? (
            <span className="ferro-form__error" role="alert">
              {printedNameError}
            </span>
          ) : null}
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">Email</span>
          <input
            type="email"
            name="ownerSignature.email"
            required
            autoComplete="email"
            value={value.ownerSignature.email}
            onChange={onSignatureField('email')}
            className="ferro-field__input"
            placeholder="jane@example.com"
            aria-invalid={
              clientEmailError || emailServerError ? true : undefined
            }
            data-testid="intake-step10-signature-email"
          />
          {clientEmailError ? (
            <span className="ferro-form__error" role="alert">
              {clientEmailError}
            </span>
          ) : null}
          {emailServerError ? (
            <span className="ferro-form__error" role="alert">
              {emailServerError}
            </span>
          ) : null}
        </label>

        {value.ownerSignature.timestamp !== '' ? (
          <p
            className="ferro-muted"
            data-testid="intake-step10-signature-timestamp"
          >
            Signed at {value.ownerSignature.timestamp}
          </p>
        ) : (
          <p
            className="ferro-muted"
            data-testid="intake-step10-signature-pending"
          >
            Timestamp is stamped once name + email are filled in.
          </p>
        )}
        {sigRootError ? (
          <span className="ferro-form__error" role="alert">
            {sigRootError}
          </span>
        ) : null}
      </fieldset>

      <label className="ferro-field ferro-intake__checkbox">
        <input
          type="checkbox"
          name="reviewedAllSteps"
          checked={value.reviewedAllSteps}
          onChange={onCheckbox('reviewedAllSteps')}
          aria-invalid={reviewedAllError ? true : undefined}
          data-testid="intake-step10-ack-reviewed"
        />
        <span>I've reviewed every section above and the details are correct.</span>
        {reviewedAllError ? (
          <span className="ferro-form__error" role="alert">
            {reviewedAllError}
          </span>
        ) : null}
      </label>

      <label className="ferro-field ferro-intake__checkbox">
        <input
          type="checkbox"
          name="moneyBackGuaranteeAcknowledged"
          checked={value.moneyBackGuaranteeAcknowledged}
          onChange={onCheckbox('moneyBackGuaranteeAcknowledged')}
          aria-invalid={mbgAckError ? true : undefined}
          data-testid="intake-step10-ack-mbg"
        />
        <span>
          I understand the 60-day money-back guarantee and my right to cancel.
        </span>
        {mbgAckError ? (
          <span className="ferro-form__error" role="alert">
            {mbgAckError}
          </span>
        ) : null}
      </label>

      <label className="ferro-field ferro-intake__checkbox">
        <input
          type="checkbox"
          name="tosAcknowledged"
          checked={value.tosAcknowledged}
          onChange={onCheckbox('tosAcknowledged')}
          aria-invalid={tosAckError ? true : undefined}
          data-testid="intake-step10-ack-tos"
        />
        <span>I agree to the Animal Lovers Platform Terms of Service.</span>
        {tosAckError ? (
          <span className="ferro-form__error" role="alert">
            {tosAckError}
          </span>
        ) : null}
      </label>

      <label className="ferro-field ferro-intake__checkbox">
        <input
          type="checkbox"
          name="privacyAcknowledged"
          checked={value.privacyAcknowledged}
          onChange={onCheckbox('privacyAcknowledged')}
          aria-invalid={privacyAckError ? true : undefined}
          data-testid="intake-step10-ack-privacy"
        />
        <span>I've read the Privacy Policy and agree to how data is handled.</span>
        {privacyAckError ? (
          <span className="ferro-form__error" role="alert">
            {privacyAckError}
          </span>
        ) : null}
      </label>

      {value.renderedKitHash !== '' ? (
        <p
          className="ferro-muted"
          data-testid="intake-step10-hash"
        >
          Review hash: <code>{value.renderedKitHash.slice(0, 16)}…</code>
        </p>
      ) : null}
      {hashServerError ? (
        <span
          className="ferro-form__error"
          role="alert"
          data-testid="intake-step10-hash-servererror"
        >
          {hashServerError}
        </span>
      ) : null}
    </form>
  );
}
